import { siteApiSlice } from "../../app/siteApiSlice";
import NamedEntity from "../../interfaces/NamedEntity";
import { CommentType } from "../../rte/Annotations";
import { PeerReviewViewModel } from "../peerReviews/peerReviewsApiSlice";
import { StimulusMaterialViewModel } from "../stimulusMaterial/stimulusMaterialApiSlice";
import { CommentedEssayViewModel } from "../writing/drafting/draftingApiSlice";
import { DraftStatus } from "../writing/writingApiSlice";

export interface ScoreReportViewModel {
	draft: DraftViewModel;
	course: CourseViewModel;
	organizer: OrganizerViewModel;
	commentedEssay: CommentedEssayViewModel;
	completedReviews: PeerReviewViewModel[];
	pureTextForWordCount: string;
	title: string;
	isProctor: boolean;
	isWriter: boolean;
	scoringLevelLabel: string;
	canScoreProctorScorableCategories: boolean;
	reviewedTestIds: number[];
	showPeerReviewSection: boolean;
	reviewCount: number;
	totalRequestedReviewCount: number;
	dateSent: string;
	showMessagesSection: boolean;
	canAccessContent: boolean;
	canDeleteDraft: boolean;
	scoringFailed: boolean;
	writerName: string;
	prompt: PromptViewModel;
	showSignLanguage: boolean;
	signLanguageVideoUrl: string;
	drafts: DraftViewModel[];
	remainingRevisions: number;
	canUserPreview: boolean;
	canUserFinalize: boolean;
	isFinalized: boolean;
	canUserRetract: boolean;
	draftScore: DraftScoreViewModel;
	draftScoreNorm: DraftScoreNormViewModel;
	showNormativeScores: boolean;
	plagiarismAnalysis: PlagiarismAnalysisViewModel;
	staticFeedback: { [key: number]: string[] };
	targetedFeedback: { [key: number]: string[] };
	llmFeedback?: LlmFeedbackViewModel;
	feedbackTutorials: { [key: number]: FeedbackTutorialViewModel[] };
	stimulusMaterials: StimulusMaterialViewModel[];
	isPrinting: boolean;
	dueDateMessage: string;
	dueDateExceeded: boolean;
	dueDateRequired: boolean;
	dueDateEnabled: boolean;
}
export interface DraftViewModel {
	id: number;
	draftNumber: number;
	status: DraftStatus;
	title: string;
	titleRequired: boolean;
	hasLegacyHtmlFormat: boolean;
	createDate: string;
	showScore: boolean;
	hasPlagiarismChecking: boolean;
}
export interface CourseViewModel {
	name: string;
	gradeId: number;
}
export interface OrganizerViewModel {
	htmlTemplate: string;
	organizerResponse: string;
	isVisibleByDefault: boolean;
}
export interface PromptViewModel {
	renderedText: string;
	title: string;
	genre: NamedEntity;
	signLanguageVideoUrl: string;
}
export interface DraftScoreViewModel {
	decimalPlacesDisplayed: number;
	cSharpScoreFormat: string;
	highchartsScoreFormat: string;
	id: number;
	totalScore: number;
	categoryScores: ScoredCategoryViewModel[];
}
export interface DraftScoreNormViewModel {
	percentileRank: number;
	stanine: number;
}
export interface PlagiarismAnalysisViewModel {
	score: number;
	annotations: PlagiarismAnnotationViewModel[];
}
export interface LlmFeedbackViewModel {
	id: number;
	feedback: { [key: number]: string[] };
}
export interface FeedbackTutorialViewModel {
	name: string;
	codeName: string;
}
export interface PlagiarismAnnotationViewModel {
	startIndex: number;
	length: number;
	snippet: string;
	url: string;
	score: number;
}

export interface WritingAnalysisViewModel {
	id: number;
	showScore: boolean;
	genre: string;
	categories: ScoredCategoryViewModel[];
	cSharpScoreFormat: string;
	decimalPlacesDisplayed: number;
	canScoreProctorScorableCategories: boolean;
	staticFeedback: { [key: number]: string[] };
	targetedFeedback: { [key: number]: string[] };
	llmFeedback?: LlmFeedbackViewModel;
	feedbackTutorials: { [key: number]: FeedbackTutorialViewModel[] };
}

export interface ScoredCategoryViewModel {
	id: number;
	score?: number;
	maxScore: number;
	name: string;
	isProctorScored: boolean;
	feedback: string;
	draftScoreCategoryId: number;
}

export interface SendReviewResponse {
	status: string;
	dateSent: string;
	totalRequestedReviewCount: number;
}

export interface DraftScoreCategoryEditModel {
	draftScoreCategoryId?: number;
	categoryId: number;
	scoreId: number;
	value: number;
	proctorFeedback: string;
}

export interface EditDraftScoreCategoryResponse {
	draftScoreCategoryId: number;
}

export interface SyncDraftCommentRequest {
	draftId: number;
	comments: RteExtendedComment[];
}

export interface RteExtendedComment {
	from: number;
	length: number;
	type: ExtendedCommentType;
	id: number;
	text: string;
}

export type ExtendedCommentType =
	| CommentType
	| "sourcesspelling"
	| "sourcesgrammar"
	| "sourcescomment";

export interface RteCommentRemaps {
	[id: string]: number;
}

export const scoreReportApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		scoreReport: build.query<ScoreReportViewModel, number>({
			query: (draftId) => ({ url: `/ScoreReport`, params: { draftId } }),
			providesTags: ["ScoreReport"],
			async onQueryStarted(draftId, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(siteApiSlice.util.invalidateTags(["Notifications", "WritingHistory"]));
				} catch (err) {}
			},
		}),
		scoreReportByEssay: build.query<ScoreReportViewModel, number>({
			query: (essayId) => ({ url: `/ScoreReport/ScoreReportByEssay`, params: { essayId } }),
			providesTags: ["ScoreReport"],
		}),
		sendReviewRequest: build.mutation<SendReviewResponse, number>({
			query: (draftId) => ({
				url: `/ScoreReport/SendReviewRequest`,
				method: "POST",
				body: { draftId },
			}),
			invalidatesTags: ["ScoreReport"],
		}),
		editDraftScoreCategory: build.mutation<
			EditDraftScoreCategoryResponse,
			DraftScoreCategoryEditModel
		>({
			query: (payload) => ({
				url: `/ScoreReport/CreateOrEditDraftScoreCategory`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: ["ScoreReport"],
		}),
		syncDraftComments: build.mutation<RteCommentRemaps[], SyncDraftCommentRequest>({
			query: (payload) => ({
				url: `/DraftComment/Sync?draftId=${payload.draftId}`,
				method: "POST",
				body: payload.comments,
			}),
			invalidatesTags: ["ScoreReport"],
		}),
		printLastScoreReports: build.query<ScoreReportViewModel[], number>({
			query: (promptId) => ({
				url: `/prompt-scorereports-ajax/${promptId}`,
			}),
			providesTags: ["CurrentCourse", "WritingHistory"],
		}),
	}),
});

export const {
	useScoreReportQuery,
	useScoreReportByEssayQuery,
	useSendReviewRequestMutation,
	useEditDraftScoreCategoryMutation,
	useSyncDraftCommentsMutation,
	useLazyPrintLastScoreReportsQuery,
} = scoreReportApiSlice;
